import { useState } from 'react';
import { Button } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { DataTable, formatDateTimeLongWithLocale } from 'remote/global';
import RemoveRedEyeOutlined from '@mui/icons-material/RemoveRedEyeOutlined';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';
import ComposedDrawerMenuAppBar from 'components/ComposedDrawerMenuAppBar';
import { useForm } from 'react-hook-form';
import { useOperatorOffersQuery } from 'api/queries/operators';
import { OperatorsInvoicesPaginationQueryParams } from 'api/types';
import { BookingItem } from 'utils/data/data-types';
import PositionDetailsModal from '@/components/modals/PositionDetailsModal/PositionDetailsModal';
import WrappedOrderDetailsModal from 'components/modals/OrderModal/WrappedOrderDetailsModal';
import { track } from '@/utils/tracking';

export function ShowOrderDetailsModalLink({
  bookingNumber,
  rideNumber,
  text,
}: {
  bookingNumber: string;
  rideNumber: number;
  text: string;
}) {
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false);
  const [bookingItem, setBookingItem] = useState<BookingItem | undefined>();

  const handleClearOrderDetailsModalState = () => {
    setIsOrderDetailsModalOpen(false);
    setBookingItem(undefined);
  };

  return (
    <>
      <Button
        variant='text'
        onClick={() => {
          setBookingItem({ bookingNumber, rideNumber });
          setIsOrderDetailsModalOpen(true);
        }}
        size='small'
        sx={{ fontSize: '14px', p: 0, height: 'auto' }}
      >
        {text}
      </Button>
      <WrappedOrderDetailsModal
        open={isOrderDetailsModalOpen}
        setOpen={setIsOrderDetailsModalOpen}
        onClose={handleClearOrderDetailsModalState}
        bookingNumber={bookingItem?.bookingNumber!}
        rideNumber={bookingItem?.rideNumber!}
      />
    </>
  );
}

function AnalyticsView() {
  const [isPositionsModalOpen, setIsPositionsModalOpen] = useState(false);
  const [orderPricingDetailsBookingNumberKey, setOrderPricingDetailsBookingNumberKey] = useState<
    string | undefined
  >();
  const formInstance = useForm<OperatorsInvoicesPaginationQueryParams>({
    defaultValues: {
      pageNo: '0',
    },
  });
  const { watch, setValue } = formInstance;
  const pageNo = watch('pageNo');
  const { data: operatorOffers, isLoading } = useOperatorOffersQuery({
    pageNo: String(pageNo),
  });
  const { t, i18n } = useTranslation();
  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: `${t('OffersView.table.header.createdAt')}`,
      minWidth: 150,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => formatDateTimeLongWithLocale(value, i18n.language),
    },
    {
      field: 'startTime',
      headerName: `${t('OffersView.table.header.startTime')}`,
      minWidth: 150,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => formatDateTimeLongWithLocale(value, i18n.language),
    },
    {
      field: 'startAddress',
      headerName: `${t('OffersView.table.header.startAddress')}`,
      minWidth: 120,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'endAddress',
      headerName: `${t('OffersView.table.header.endAddress')}`,
      minWidth: 120,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'totalLengthInKm',
      headerName: `${t('OffersView.table.header.totalLengthInKm')}`,
      minWidth: 110,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => value,
    },
    {
      field: 'passengerCount',
      headerName: `${t('OffersView.table.header.passengerCount')}`,
      minWidth: 110,
      flex: 1,
      sortable: false,
    },
    {
      field: 'vehicleMake',
      headerName: `${t('OffersView.table.header.vehicleMake')}`,
      minWidth: 150,
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => `${row.vehicleMake} ${row.vehicleModel}`,
    },
    {
      field: 'operatorPriceInclVat',
      headerName: `${t('OffersView.table.header.operatorPriceInclVat')}`,
      minWidth: 120,
      flex: 1,
      sortable: false,
      valueGetter: ({ row }) => `${row.operatorPriceInclVat}€`,
    },
    {
      field: 'rank',
      headerName: `${t('OffersView.table.header.rank')}`,
      minWidth: 140,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          sx={{ fontSize: '14px', padding: 0 }}
          variant='text'
          onClick={async () => {
            setIsPositionsModalOpen(true);
            setOrderPricingDetailsBookingNumberKey(row.refNumber);
            track.partner_button_click(`VIEW_POSITION-${row.refNumber}`);
          }}
        >
          <RemoveRedEyeOutlined fontSize='small' sx={{ mr: 1 }} />
          {row.rank}/{row.totalOffers}
        </Button>
      ),
    },
  ];

  const handleClearTransparencyModalState = () => {
    setIsPositionsModalOpen(false);
    setOrderPricingDetailsBookingNumberKey(undefined);
  };

  return (
    <>
      <PositionDetailsModal
        open={isPositionsModalOpen}
        setOpen={setIsPositionsModalOpen}
        onClose={handleClearTransparencyModalState}
        bookingNumber={orderPricingDetailsBookingNumberKey!}
      />
      <ComposedDrawerMenuAppBar title={t('OffersView.title')} />
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <DataTable
          rows={operatorOffers ?? []}
          columns={columns}
          loading={isLoading}
          getRowId={() => uuidv4()}
          onPageChange={(page) => setValue('pageNo', String(page))}
          t={t}
        />
      </Box>
    </>
  );
}

export default AnalyticsView;
